/* eslint-disable indent */
import React from 'react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useMeasurement } from '~/utility/BinSentry-ui-utility';
import { makeStyles, Typography, Button, useTheme, InputAdornment, TextField } from '~/lib/bn-material-ui';
import { useOrdersDialogDispatch, useOrdersDialogStateSelector } from '../context/orders-dialog-context';
import { BinLevelProfile } from '~/bins/bin-level-profile';
import { calculateScale } from '~/bins/bin-level/bin-circular-profile-svg';
import { shouldIncludeOrderInAdditionalVolume } from '../context/orders-dialog-context-utilities';
import { useBinCapacity } from '~/bins/bin-capacity';

const TOP_MARGIN_PX = 20;

const useStyles = makeStyles(({ palette }) => ({
  subdued: {
    color: palette.gray[60],
  },
  binsContainer: {
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'row',
    gap: '2rem',
    flexGrow: 1,
  },
  binContainer: {
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '18rem',
    paddingTop: '.5rem',
    margin: 'auto',
  },
  binProfileContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    marginRight: '1rem',
  },
  binPolygon: {
    fill: 'white',
  },
  binAllocationForm: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '.5rem',
    padding: '.5rem',
    gap: '.5rem',
  },
  capacityText: {
    fontSize: '.9em',
    color: palette.gray[60],
  },
  orderAllocationField: {
    minWidth: '7.5rem',
    '& .MuiSvgIcon-root': {
      color: palette.secondary.main,
    },
    '& .MuiInput-underline': {
      '&:before': {
        borderColor: palette.secondary.main,
      },
      '&:hover:before': {
        borderBottomColor: palette.secondary.main,
      },
    },
  },
}));

const Bin = React.memo(
  function Bin({ styles, binId, orderMeasurement, binEntity, widthPx, heightPx, scale }) {
    const totalCapacity = binEntity.properties.volumes.fullTotal;
    const dispatch = useOrdersDialogDispatch();
    const theme = useTheme();
    const [selectedOrderEntity, feeds, unsavedFeedId, forecast, binLevelEntity, selectedBarnDaysAllocations, selectedAllocations, createOrEditSirenAction, submitting, isoDate] = useOrdersDialogStateSelector([
      'selectedOrderEntity',
      'feeds',
      'unsavedChanges.feedId',
      `forecastsByBinId['${binId}']`,
      `binLevelsByBinId['${binId}']`,
      'selectedBarnDaysAllocations',
      'selectedAllocations',
      'createOrEditSirenAction',
      'submitting',
      'isoDate',
    ]);

    const actionFieldName = `binAllocations[${binId}]`;
    const [allocatedVolume] = useOrdersDialogStateSelector([
      (state) => state.unsavedChanges?.[actionFieldName],
    ]);

    const allocationsForThisBin = selectedBarnDaysAllocations.filter(allocation => allocation.binId === binId);
    const selectedOrderEntityId = selectedOrderEntity?.getLinkByRel('self').href;
    const notBeingEditedAllocations = allocationsForThisBin.filter(allocation => selectedOrderEntityId !== allocation.orderId);
    const selectedAllocationForThisBin = selectedAllocations?.find(allocation => allocation.binId === binId);
    const hasInput = typeof allocatedVolume === 'number' || allocatedVolume === null;
    const allocationVolume = hasInput ? allocatedVolume : parseFloat(selectedAllocationForThisBin?.volume);

    const currentTimeLuxon = DateTime.fromISO(isoDate);
    const timeDistanceToLevel = Math.abs(
      DateTime.fromISO(binLevelEntity?.properties.publishedAt).diff(currentTimeLuxon).as('seconds'),
    );
    const timeDistanceToForecast = Math.abs(
      DateTime.fromISO(forecast?.publishedAt).diff(currentTimeLuxon).as('seconds'),
    );

    const forecastNearer = timeDistanceToForecast < timeDistanceToLevel;
    const totalFeedsInBin = [
      ...forecastNearer ?
        [{ volume: forecast.low, color: theme.palette.indigo[60] }] :
        [{ volume: binLevelEntity?.properties.volume, color: theme.palette.indigo[60] }],

      ...notBeingEditedAllocations
        .map(allocation => (shouldIncludeOrderInAdditionalVolume(allocation.orderEntity) ?
          { volume: allocation.volume, color: theme.palette.indigo[10] } :
          undefined
        ))
        .filter(item => item),

      ...Number.isFinite(allocationVolume) && shouldIncludeOrderInAdditionalVolume(selectedOrderEntity) ?
        [{ volume: allocationVolume, color: theme.palette.indigo[10] }] : [],
    ];

    const totalFeedInBin = _.sumBy(totalFeedsInBin, 'volume') || 0;
    const totalFeedInBinMinusSelectedOrder = totalFeedInBin - (allocationVolume || 0);
    const overfillPredicted = totalFeedInBin > totalCapacity;
    const feedToFillBin = Math.max(Math.floor(totalCapacity - totalFeedInBinMinusSelectedOrder), 0);

    const selectedFeedId = unsavedFeedId || selectedOrderEntity?.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href;
    const feedEntity = feeds?.find(feed => feed.getLinkByRel('self').href === selectedFeedId);
    const bulkDensity = feedEntity?.properties.bulkDensity;
    const convertedOrderAmount = Number.isFinite(allocationVolume) && Number.isFinite(bulkDensity) && orderMeasurement.convert({
      volume: allocationVolume,
      bulkDensity,
    });

    const disabled = !createOrEditSirenAction || submitting > 0 || !feedEntity;
    const showOverFillMessage = !disabled && (selectedAllocationForThisBin || hasInput) && overfillPredicted;
    const { binCapacity, unit: binCapacityUnit } = useBinCapacity({ binHref: binId }) || {};
    const hideCapacity = !feedEntity || binCapacityUnit === '%';

    const convertedFillAmount = orderMeasurement.convert({ volume: feedToFillBin, bulkDensity });
    const fillRoundedToNearestQuarter = Math.floor(convertedFillAmount * 4) / 4;
    const roundedVolumeToFillBin = orderMeasurement.reverse({
      volume: fillRoundedToNearestQuarter,
      weight: fillRoundedToNearestQuarter,
      bulkDensity,
    });
    const orderQuantityFillsBin = roundedVolumeToFillBin === allocationVolume;

    return (
      <div className={styles.binContainer}>
        <div className={styles.binProfileContainer} style={{ width: `${widthPx}px` }}>
          <BinLevelProfile
            binHref={binEntity?.getLink('self')?.href}
            heightPx={heightPx}
            widthPx={widthPx}
            scale={scale}
            feeds={totalFeedsInBin}
            binPolygonClassName={styles.binPolygon}
          />
        </div>
        <div className={styles.binAllocationForm}>
          <Typography variant='body1'>{binEntity.properties.name}</Typography>
          {!hideCapacity && (
            <Typography variant='body1' className={styles.capacityText}>
              Capacity: {binCapacity}
            </Typography>
          )}
          <TextField
            label='Order Amount'
            type='number'
            size='small'
            InputProps={{
              endAdornment: orderMeasurement.unit ? <InputAdornment position='end'>{orderMeasurement.unit}</InputAdornment> : null,
            }}
            className={styles.orderAllocationField}
            value={convertedOrderAmount}
            InputLabelProps={{ shrink: Number.isFinite(allocationVolume) }}
            { ...showOverFillMessage ?
              {
                error: 'Overfill predicted',
                helperText: 'Overfill predicted',
              } : {}
            }
            disabled={disabled}
            onChange={(e) => {
              const valueFloat = parseFloat(e.target.value);
              const volumeValue = orderMeasurement.reverse(
                {
                  ...(orderMeasurement.metric === 'weight' ?
                    { weight: valueFloat } :
                    { volume: valueFloat }),
                  bulkDensity,
                },
              );
              dispatch({
                type: 'set-unsaved-change-data',
                data: {
                  [actionFieldName]: Number.isFinite(volumeValue) ? Math.max(volumeValue, 0) : null,
                },
              });
            }}
          />
          <div>
            <Button
              size='small'
              variant='outlined'
              disabled={disabled || orderQuantityFillsBin}
              onClick={() => {
                dispatch({
                  type: 'set-unsaved-change-data',
                  data: {
                    [actionFieldName]: roundedVolumeToFillBin || 0,
                  },
                });
              }}
            >Fill</Button>
          </div>
        </div>
      </div>
    );
  },
);

export function OrdersDialogPhysicalBins({ profileWidthPx, profileHeightPx }) {
  const styles = useStyles();
  const [selectedBarnsBins] = useOrdersDialogStateSelector('selectedBarnsBins');
  const bins = selectedBarnsBins
    .sort((binA, binB) =>
      binA.properties.name.localeCompare(binB.properties.name, 'en', { numeric: true }),
    );
  const orderMeasurement = useMeasurement({ name: 'orderAmount' });

  const scale = _.min(bins.map(bin => calculateScale({
    binDimensions: bin.properties.dimensions,
    widthPx: profileWidthPx,
    heightPx: profileHeightPx - TOP_MARGIN_PX,
  })));

  const binComponents = bins.map(binEntity => {
    const binId = binEntity.getLinkByRel('self').href;
    return (
      <Bin
        key={binId}
        binId={binId}
        binEntity={binEntity}
        orderMeasurement={orderMeasurement}
        styles={styles}
        widthPx={profileWidthPx}
        heightPx={profileHeightPx - TOP_MARGIN_PX}
        scale={scale}
      />
    );
  });

  return (
    <div className={styles.binsContainer}>
      {binComponents}
    </div>
  );
}
